import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Trash2, CheckCircle, ListTodo, FileText } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const EMPTY_TASK = { task_id: '', task_title: '', description: '', hours: 0, category: '', notes: '' };

export const TimesheetTab = () => {
    const [timesheetTasks, setTimesheetTasks] = useState([{ ...EMPTY_TASK }]);
    const [timesheetSummary, setTimesheetSummary] = useState('');
    const [assignedTasks, setAssignedTasks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [timesheetHistory, setTimesheetHistory] = useState([]);

    const activeTasks = useMemo(() => assignedTasks.filter(t => t.status !== 'completed'), [assignedTasks]);
    const timesheetTotalHours = useMemo(() => timesheetTasks.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0).toFixed(1), [timesheetTasks]);

    const fetchData = useCallback(async () => {
        try {
            const [tasksRes, catRes, tsRes] = await Promise.all([
                apiClient.get('/hr/my-tasks').catch(() => ({ data: { tasks: [] } })),
                apiClient.get('/hr/timesheet-categories').catch(() => ({ data: { categories: [] } })),
                apiClient.get('/hr/my-timesheets').catch(() => ({ data: { timesheets: [] } })),
            ]);
            setAssignedTasks(tasksRes.data?.tasks || []);
            setCategories(catRes.data?.categories || []);
            setTimesheetHistory(tsRes.data?.timesheets || []);
        } catch (e) { logger.warn('Timesheet fetch error', e); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSubmitTimesheet = async () => {
        const validTasks = timesheetTasks.filter(t => (t.description?.trim() || t.task_title?.trim()) && t.hours > 0);
        if (validTasks.length === 0) { toast.error('Add at least one task with hours'); return; }
        try {
            await apiClient.post('/hr/timesheet', { tasks: validTasks, summary: timesheetSummary, date: new Date().toISOString().split('T')[0] });
            toast.success('Timesheet submitted!');
            setTimesheetTasks([{ ...EMPTY_TASK }]);
            setTimesheetSummary('');
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit timesheet'); }
    };

    const handlePickTask = (task) => {
        const existing = timesheetTasks.find(t => t.task_id === task.id);
        if (existing) { toast.info('Task already added'); return; }
        const newEntry = { task_id: task.id, task_title: task.title, description: task.description || '', hours: 0, category: task.category || '', notes: '' };
        const emptySlot = timesheetTasks.findIndex(t => !t.task_id && !t.description?.trim() && !t.task_title?.trim());
        if (emptySlot >= 0) {
            const updated = [...timesheetTasks];
            updated[emptySlot] = newEntry;
            setTimesheetTasks(updated);
        } else {
            setTimesheetTasks([...timesheetTasks, newEntry]);
        }
    };

    const handleUpdateTaskStatus = async (taskId, newStatus) => {
        try {
            await apiClient.put(`/hr/tasks/${taskId}`, { status: newStatus });
            setAssignedTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
            toast.success(`Task ${newStatus.replace(/_/g, ' ')}`);
        } catch { toast.error('Failed to update task'); }
    };

    const updateTask = (index, field, value) => {
        const updated = [...timesheetTasks];
        updated[index][field] = value;
        setTimesheetTasks(updated);
    };

    return (
        <div className="space-y-4">
            {activeTasks.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2"><ListTodo className="h-4 w-4" /> Assigned Tasks</CardTitle>
                        <CardDescription>Pick a task to log time against it</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                            {activeTasks.map(task => (
                                <div key={task.id} className="flex items-center justify-between p-2.5 bg-muted/40 rounded-lg hover:bg-muted/60 transition-colors" data-testid={`assigned-task-${task.id}`}>
                                    <div className="flex-1 min-w-0 mr-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium truncate">{task.title}</span>
                                            <Badge variant={task.priority === 'urgent' ? 'destructive' : task.priority === 'high' ? 'default' : 'secondary'} className="text-[10px] shrink-0">{task.priority}</Badge>
                                            <Badge variant="outline" className="text-[10px] shrink-0">{task.category}</Badge>
                                        </div>
                                        {task.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{task.description}</p>}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <select value={task.status} onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value)} className="text-xs px-1.5 py-1 rounded border bg-background">
                                            <option value="open">Open</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="completed">Done</option>
                                        </select>
                                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handlePickTask(task)}><Plus className="h-3 w-3 mr-1" />Log</Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Daily Timesheet</CardTitle>
                    <CardDescription>Log tasks & hours. You can pick assigned tasks or add custom entries.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    {timesheetTasks.map((task, i) => (
                        <div key={i} className="p-3 border border-border/50 rounded-lg space-y-2 bg-muted/20" data-testid={`timesheet-entry-${i}`}>
                            <div className="flex gap-2 items-start">
                                <div className="flex-1">
                                    <input type="text" placeholder={task.task_id ? task.task_title : "Task name / description"} value={task.task_title || task.description || ''} readOnly={!!task.task_id} onChange={(e) => { if (!task.task_id) { updateTask(i, 'description', e.target.value); updateTask(i, 'task_title', e.target.value); } }} className={`w-full px-3 py-2 rounded-md border bg-background text-sm ${task.task_id ? 'bg-muted/50 cursor-default' : ''}`} data-testid={`timesheet-task-${i}`} />
                                </div>
                                <div className="w-20">
                                    <input type="number" placeholder="Hrs" step="0.5" min="0" max="12" value={task.hours || ''} onChange={(e) => updateTask(i, 'hours', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 rounded-md border bg-background text-sm text-center" />
                                </div>
                                <div className="w-32">
                                    <select value={task.category} onChange={(e) => updateTask(i, 'category', e.target.value)} className="w-full px-2 py-2 rounded-md border bg-background text-sm">
                                        <option value="">Category</option>
                                        {categories.map(c => <option key={c} value={c.toLowerCase().replace(/ /g, '_')}>{c}</option>)}
                                    </select>
                                </div>
                                {timesheetTasks.length > 1 && (
                                    <Button variant="ghost" size="sm" className="text-red-500 h-9" onClick={() => setTimesheetTasks(timesheetTasks.filter((_, idx) => idx !== i))}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                            <textarea placeholder="Notes — what specifically did you work on?" value={task.notes || ''} onChange={(e) => updateTask(i, 'notes', e.target.value)} className="w-full px-3 py-1.5 rounded-md border bg-background text-xs min-h-[36px] resize-none" rows={1} />
                        </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setTimesheetTasks([...timesheetTasks, { ...EMPTY_TASK }])}>
                        <Plus className="h-4 w-4 mr-1" />Add Custom Task
                    </Button>
                    <div>
                        <label className="text-sm font-medium mb-1 block">Daily Summary</label>
                        <textarea value={timesheetSummary} onChange={(e) => setTimesheetSummary(e.target.value)} placeholder="Brief summary of what you accomplished today..." className="w-full px-3 py-2 rounded-md border bg-background text-sm min-h-[50px]" data-testid="timesheet-summary" />
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Total: <span className="font-bold">{timesheetTotalHours} hours</span></p>
                        <Button onClick={handleSubmitTimesheet} data-testid="submit-timesheet-btn"><CheckCircle className="h-4 w-4 mr-2" />Submit Timesheet</Button>
                    </div>
                </CardContent>
            </Card>

            {timesheetHistory.length > 0 && (
                <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Recent Submissions</CardTitle></CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[200px]">
                            <div className="space-y-2">
                                {timesheetHistory.slice(0, 7).map(ts => (
                                    <div key={ts.id} className="flex items-center justify-between p-2.5 bg-muted/30 rounded-lg">
                                        <div><p className="text-sm font-medium">{ts.date}</p><p className="text-xs text-muted-foreground">{ts.tasks?.length || 0} tasks</p></div>
                                        <div className="text-right"><p className="text-sm font-bold">{ts.total_logged_hours?.toFixed(1) || 0}h</p></div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
